import { useEffect, useState } from "react";
import { CalendarDays, Clock, MapPin, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const Events = () => {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    fetch(`${API}/events`)
      .then((r) => r.json())
      .then(setEvents)
      .catch(() => {});
  }, []);

  return (
    <section
      id="events"
      data-testid="events-section"
      className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40"
    >
      <Reveal>
        <p className="mb-6 text-xs font-semibold uppercase tracking-[0.25em] text-plum">
          04 · Gatherings & Events
        </p>
        <div className="flex flex-wrap items-end justify-between gap-8">
          <h2 className="max-w-2xl font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl md:text-6xl">
            Circles, walks & <em className="text-plum">making days.</em>
          </h2>
          <p className="max-w-sm text-base leading-relaxed text-slate2">
            Everything below is free for members. Guests are always welcome at
            storytelling — just come a little early.
          </p>
        </div>
      </Reveal>

      <div className="mt-16" data-testid="events-list">
        {events.length === 0 && (
          <p data-testid="events-empty" className="border-t border-ink/10 py-10 font-serif text-2xl italic text-slate2">
            New gatherings are being planned — check back soon.
          </p>
        )}
        {events.map((e, i) => {
          const [y, m, d] = (e.date || "").split("-");
          return (
            <Reveal key={e.id} delay={0.06 * i}>
              <div
                data-testid={`event-row-${i}`}
                className="group flex flex-col gap-6 border-t border-ink/10 py-8 transition-colors duration-300 last:border-b hover:bg-sand/60 sm:flex-row sm:items-center sm:gap-10"
              >
                <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-2xl bg-plum text-cream sm:ml-2">
                  <span className="font-serif text-3xl leading-none">{d}</span>
                  <span className="mt-1 text-[11px] font-semibold uppercase tracking-[0.2em]">
                    {MONTHS[parseInt(m, 10) - 1]} {y}
                  </span>
                </div>
                <div className="flex-1 px-2 sm:px-0">
                  <h3 className="font-serif text-2xl text-ink transition-colors duration-300 group-hover:text-plum md:text-3xl">
                    {e.title}
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-5 text-sm text-slate2">
                    {e.time && (
                      <span className="inline-flex items-center gap-1.5">
                        <Clock size={14} className="text-terracotta" /> {e.time}
                      </span>
                    )}
                    {e.location && (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin size={14} className="text-terracotta" /> {e.location}
                      </span>
                    )}
                  </div>
                  {e.description && (
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate2">{e.description}</p>
                  )}
                </div>
                <a
                  href="#visit"
                  data-testid={`event-cta-${i}`}
                  className="inline-flex shrink-0 items-center gap-2 self-start rounded-full border border-ink/25 px-6 py-3 text-sm font-semibold text-ink transition-colors duration-300 hover:border-plum hover:text-plum sm:self-center"
                >
                  Join us <ArrowUpRight size={15} />
                </a>
              </div>
            </Reveal>
          );
        })}
      </div>

      <Reveal delay={0.2}>
        <p className="mt-10 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate2">
          <CalendarDays size={14} className="text-plum" />
          The calendar is updated by the library team every week
        </p>
      </Reveal>
    </section>
  );
};
